import { useEffect, useEffectEvent } from 'react';

export interface UseSubmitShortcutParams {
  /** The form's submit handler; `true` submits with "create another". */
  handleSubmit: (shouldCreateAnother: boolean) => Promise<void>;
  /** Whether this form is the visible top of the create-slideout stack. */
  isTop: boolean;
  isSubmitting: boolean;
  showCreateAnother: boolean;
}

/**
 * Handle CMD+Enter (Mac) / CTRL+Enter (Windows/Linux) to submit with
 * "create another" — only when that option is available and this form is
 * the visible top of the stack. An Effect Event so the listener sees the
 * latest handler without re-subscribing.
 */
export function useSubmitShortcut({
  handleSubmit,
  isTop,
  isSubmitting,
  showCreateAnother,
}: UseSubmitShortcutParams): void {
  const onShortcutKeydown = useEffectEvent((e: KeyboardEvent) => {
    if (!isTop || isSubmitting) {
      return;
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void handleSubmit(true);
    }
  });
  useEffect(() => {
    if (!showCreateAnother || !isTop) {
      return;
    }
    const listener = (e: KeyboardEvent) => onShortcutKeydown(e);
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
    // An Effect Event is never a dependency (REACT_19_PATTERNS § 4.1); the
    // installed react-hooks plugin predates useEffectEvent and can't tell.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreateAnother, isTop]);
}
